import React, { useContext, useEffect, useState } from 'react';
import { safeRedirect } from '@ordermentum/ui-shared/helpers/urls';
import { Theme, ThemeContext } from '@ordermentum/ui-shared/utilities/theme';
import OTP from '@ordermentum/ui-shared/components/OTP';
import Spinner from '@ordermentum/ui-shared/foundations/Spinner';
import { AUTH_URL } from '@ordermentum/ui-constants';
import {
  ColorCodes,
  colorsForTheme,
} from '@ordermentum/ui-shared/foundations/color';
import { ThemeProvider } from 'emotion-theming';
import axios from 'axios';
import { Location } from 'history';
import qs from 'qs';
import Banner from '@ordermentum/ui-shared/atoms/Banner';
import Button, {
  LinkButton,
} from '@ordermentum/ui-shared/components/Button/Button';
import {
  MFAContainer,
  MFADescription,
  MFAHeader,
  MFAHelpDescription,
  MFAHelpHeader,
  OTPWrapper,
  SpinnerWrapper,
} from './MFAForm.styles';
import MFAResend from './MFAResend';
import { ErrorBannerWrapper } from './index.styles';

enum MFAMethod {
  TOTP = 'totp',
  SMS = 'sms',
  EMAIL = 'email',
}

export enum MFAAction {
  PASSWORD_RESET = 'password_reset',
  LOGIN = 'login',
  SOCIAL_LOGIN = 'social_login',
}

type themeStyles = {
  headerColor: ColorCodes;
  descriptionColor: ColorCodes;
  helpHeaderColor: ColorCodes;
  helpDescriptionColor: ColorCodes;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  return {
    headerColor: colors.STORM,
    descriptionColor: colors.STORM,
    helpHeaderColor: colors.STORM,
    helpDescriptionColor: colors.STORM,
  };
};

const validCode = (code: string) => code.length >= 6;

export const MFAForm = ({ location }: { location: Location }) => {
  const { userId, method, target, state, action, loginUrl, destination } =
    location.query;
  const [reqError, setReqError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isInvalidCode, setIsInvalidCode] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const themeContext = useContext(ThemeContext);
  const theme = getTheme(themeContext);

  const verifyCode = async () => {
    try {
      setIsSubmitting(true);
      if (action === MFAAction.LOGIN)
        await axios.post('/v1/auth', {
          code,
          state,
          userId,
        });
      if (action === MFAAction.PASSWORD_RESET) {
        const { passwordResetToken } = location.query;
        const query = qs.stringify({
          password_reset_token: passwordResetToken,
        });
        await axios.put(`/user/password?${query}`, {
          code,
          state,
          userId,
        });
      }
      if (destination) {
        window.location.href = safeRedirect(destination as string);
      } else {
        window.location.reload();
      }
    } catch (err) {
      setIsInvalidCode(true);
      setIsSubmitting(false);
      setCode('');
    }
  };

  const resendCode = async () => {
    try {
      await axios.post(`${AUTH_URL}/v1/mfa/generate`, {
        state,
        userId,
      });
    } catch (err) {
      setReqError('Unable to resend code. Please try again later.');
    }
  };

  useEffect(() => {
    if (validCode(code) && !isSubmitting && !isInvalidCode) {
      verifyCode();
    }
  }, [code]);

  const isAuthenticator = method === MFAMethod.TOTP;

  return (
    <ThemeProvider theme={theme}>
      <MFAContainer>
        <MFAHeader>Authentication code</MFAHeader>
        {reqError && (
          <ErrorBannerWrapper>
            <Banner type="warning" noIcon dismiss={false}>
              {reqError}
            </Banner>
          </ErrorBannerWrapper>
        )}
        <MFADescription>
          {method === MFAMethod.TOTP &&
            'Enter 6-digit code from your authenticator app'}
          {(method === MFAMethod.EMAIL || method === MFAMethod.SMS) &&
            `We've sent a 6-digit authentication code to ${target}. Please enter the code below`}
        </MFADescription>
        {isSubmitting ? (
          <SpinnerWrapper>
            <Spinner size="XXXL" color="orange" full={false} />
          </SpinnerWrapper>
        ) : (
          <OTPWrapper data-testid="otp-wrapper">
            <OTP
              hasError={isInvalidCode}
              value={code}
              onChange={(value: string) => {
                setIsInvalidCode(false);
                setCode(value);
              }}
            />
          </OTPWrapper>
        )}
        {!isAuthenticator ? (
          <MFAResend
            onCodeResend={() => {
              setIsInvalidCode(false);
              resendCode();
            }}
          />
        ) : null}
        <MFAHelpHeader>Having trouble?</MFAHelpHeader>
        <MFAHelpDescription>
          If you&apos;re having trouble accessing the device you use for
          multi-factor authentication, please contact our{' '}
          <Button
            as="a"
            variant="text-link"
            href="mailto:support@ordermentum.com"
          >
            support team
          </Button>
          .
        </MFAHelpDescription>
        <LinkButton to={(loginUrl as string) || '/sign-in'} variant="tertiary">
          Back to login
        </LinkButton>
      </MFAContainer>
    </ThemeProvider>
  );
};

export default MFAForm;
